import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import { SCHEMAS } from "@/pages/admin/schemas";

function toInput(field, val) {
  if (val == null) return "";
  if (field.type === "tags" || field.type === "list") return Array.isArray(val) ? val.join(field.type === "tags" ? ", " : "\n") : val;
  return val;
}
function fromInput(field, raw) {
  if (field.type === "number") return raw === "" ? null : Number(raw);
  if (field.type === "tags") return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
  if (field.type === "list") return String(raw).split("\n").map((s) => s.trim()).filter(Boolean);
  return raw;
}

export default function Collection() {
  const { name } = useParams();
  const schema = SCHEMAS[name];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // object or {} for new
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/${name}${q ? `?q=${encodeURIComponent(q)}` : ""}`); setItems(data); }
    catch (e) { toast.error("Failed to load"); } finally { setLoading(false); }
  }, [name, q]);

  useEffect(() => { setQ(""); }, [name]);
  useEffect(() => { load(); }, [load]);

  if (!schema) return <div className="text-slate-400">Unknown section</div>;

  const openNew = () => { setForm({}); setEditing({}); };
  const openEdit = (item) => {
    const f = {};
    schema.fields.forEach((fl) => { f[fl.k] = toInput(fl, item[fl.k]); });
    setForm(f); setEditing(item);
  };

  const save = async () => {
    const payload = {};
    schema.fields.forEach((fl) => { payload[fl.k] = fromInput(fl, form[fl.k]); });
    try {
      if (editing.id) await api.put(`/admin/${name}/${editing.id}`, payload);
      else await api.post(`/admin/${name}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const quickStage = async (item, value) => {
    try { await api.put(`/admin/${name}/${item.id}`, { [schema.statusField]: value }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  const del = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/${name}/${item.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`admin-collection-${name}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold">{schema.title}</h1>
          <p className="text-slate-400 text-sm mt-1">{items.length} items</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 bg-white/5 border-white/10 w-48" />
          </div>
          {schema.editable && <Button data-testid="admin-new-btn" onClick={openNew} className="bg-brand-emerald hover:bg-[#C62828] text-white"><Plus size={16} className="mr-1" />New</Button>}
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-left text-slate-400">
              <tr>{schema.columns.map((c) => <th key={c} className="px-4 py-3 font-medium capitalize">{c.replace(/_/g, " ")}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={schema.columns.length + 1} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={schema.columns.length + 1} className="px-4 py-10 text-center text-slate-400">No items yet.</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-row-${item.id}`}>
                  {schema.columns.map((c) => (
                    <td key={c} className="px-4 py-3">
                      {c === schema.statusField ? (
                        <Select value={item[c] || schema.statusOptions?.[0]} onValueChange={(v) => quickStage(item, v)}>
                          <SelectTrigger data-testid={`admin-stage-${item.id}`} className="h-8 w-40 bg-white/5 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="glass-strong border-white/10">{schema.statusOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : Array.isArray(item[c]) ? item[c].join(", ") : String(item[c] ?? "—").slice(0, 60)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {schema.fileField && item[schema.fileField] && (
                      <a href={`${API}/files/${item[schema.fileField]}?auth=${getToken()}`} target="_blank" rel="noreferrer"
                        className="inline-flex p-1.5 rounded-lg hover:bg-white/10 text-brand-blue" title="Download resume" data-testid={`admin-download-${item.id}`}><Download size={16} /></a>
                    )}
                    <button onClick={() => openEdit(item)} className="inline-flex p-1.5 rounded-lg hover:bg-white/10 text-slate-300" data-testid={`admin-edit-${item.id}`} title="View / Edit"><Pencil size={16} /></button>
                    <button onClick={() => del(item)} className="inline-flex p-1.5 rounded-lg hover:bg-white/10 text-red-400" data-testid={`admin-delete-${item.id}`} title="Delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="glass-strong border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? `Edit ${schema.title}` : `New ${schema.title}`}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2 mt-2">
            {schema.fields.map((fl) => (
              <div key={fl.k} className={fl.big || fl.type === "textarea" || fl.type === "list" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{fl.label}{fl.required && " *"}</Label>
                {fl.type === "select" ? (
                  <Select value={form[fl.k] || ""} onValueChange={(v) => setForm((f) => ({ ...f, [fl.k]: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10">{fl.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : fl.type === "textarea" || fl.type === "list" ? (
                  <Textarea value={form[fl.k] || ""} onChange={(e) => setForm((f) => ({ ...f, [fl.k]: e.target.value }))} rows={fl.big ? 8 : 3} className="bg-white/5 border-white/10 mt-1" disabled={!schema.editable && fl.k !== schema.statusField} />
                ) : (
                  <Input type={fl.type === "number" ? "number" : "text"} value={form[fl.k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [fl.k]: e.target.value }))} className="bg-white/5 border-white/10 mt-1" disabled={!schema.editable && fl.k !== schema.statusField} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-white/15" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="admin-save-btn" onClick={save} className="bg-brand-emerald hover:bg-[#C62828] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
